// Form response schemas — plan §11.2 + §6.1 batch upsert + Ek A.
// Discriminated union per question type — server snapshot'tan question_type okur,
// matching discriminant ile validate eder.

import { z } from "zod";
import { EVIDENCE_SCAN_STATUSES } from "./kpi.js";
import { PARTICIPANT_STATUSES } from "../constants/index.js";
import { QuestionType } from "./questions.js";
import { PeriodType } from "./period.js";

// Tek bir cevap değeri — tipe göre 10 farklı shape (§11.2 + Ek A)
export const FormValueSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("text"),
    value: z.string().max(50_000), // questions.options.maxLength ile override edilebilir (soft check)
  }),
  z.object({
    type: z.literal("numeric"),
    value: z.number().finite(),
  }),
  z.object({
    // M5.5 — single seçim + opsiyonel custom metin ("Diğer, açıklayınız" pattern'i)
    type: z.literal("single"),
    value: z.object({
      optionId: z.string().min(1),
      customInput: z.string().max(1000).optional(),
    }),
  }),
  z.object({
    // M5.5 — multi seçim + opsiyonel custom metinler (optionId → text map)
    type: z.literal("multi"),
    value: z.object({
      optionIds: z.array(z.string().min(1)),
      customInputs: z.record(z.string(), z.string().max(1000)).optional(),
    }),
  }),
  z.object({
    type: z.literal("date"),
    // Tam tarih (YYYY-MM-DD) ya da ay/yıl (YYYY-MM, `format: "MM-YYYY"` sorusu — Finar İ8).
    // Sorunun biçimine uygunluk sunucuda snapshot'la denetlenir (services/responses.ts).
    value: z
      .string()
      .regex(/^\d{4}-\d{2}(-\d{2})?$/, "Tarih YYYY-AA-GG (ay/yıl sorusunda YYYY-AA) olmalı"),
  }),
  z.object({
    type: z.literal("file"),
    value: z.array(z.string().uuid()), // attachment_ids
  }),
  z.object({
    type: z.literal("table"),
    value: z.array(z.record(z.string(), z.unknown())),
  }),
  z.object({
    type: z.literal("percent"),
    // Toplam 100 (±0.1 tolerans) — boş ({}) kabul (taslak)
    value: z.record(z.string(), z.number().finite()).refine(
      (v) => {
        const entries = Object.values(v);
        if (entries.length === 0) return true;
        const sum = entries.reduce((a, b) => a + b, 0);
        return Math.abs(sum - 100) < 0.1;
      },
      { message: "Yüzde dağılımı toplamı 100 olmalı (tolerans ±0.1)" },
    ),
  }),
  z.object({
    type: z.literal("repeater"),
    value: z.array(z.record(z.string(), z.unknown())),
  }),
  z.object({
    // M15 composite — { partKey: parçanın kendi değeri }. Parça-bazlı derin doğrulama
    // responses.ts'te snapshot'taki part.options.type ile yapılır (şema burada gevşek).
    type: z.literal("composite"),
    value: z.record(z.string(), z.unknown()),
  }),
]);

export type FormValue = z.infer<typeof FormValueSchema>;

// Response tipi — form_responses tablosundaki bir satır (FE'ye dönen şekil)
export const FormResponseSchema = z.object({
  questionCode: z.string().min(1),
  // Tek kaynak: schemas/questions.ts QuestionType (elle kopya drift'i M15'te kapatıldı)
  questionType: QuestionType,
  value: z.unknown().nullable(), // tipe göre değişir — FormValueSchema'yla validate edilir
  consultantNote: z.string().nullable(),
  updatedAt: z.string().datetime(),
});
export type FormResponse = z.infer<typeof FormResponseSchema>;

/** Danışman notu üst sınırı — öğe şeması + FE textarea `maxLength` tek kaynağı. */
export const CONSULTANT_NOTE_MAX_LENGTH = 10_000;

// Batch upsert request item — client tarafı ne gönderir
// §6.1: { project_id, question_code, value, updated_at }
// updated_at: client local optimistic timestamp. Server last-write-wins compare eder.
export const FormResponseUpsertItemSchema = z.object({
  questionCode: z.string().min(1),
  value: z.unknown().nullable(),
  consultantNote: z
    .string()
    .max(CONSULTANT_NOTE_MAX_LENGTH, "Not en fazla 10.000 karakter olabilir")
    .optional()
    .nullable(),
  clientUpdatedAt: z.string().datetime(), // ISO8601 — optimistic timestamp
});
export type FormResponseUpsertItem = z.infer<typeof FormResponseUpsertItemSchema>;

/** Tek PUT'taki öğe tavanı — FE flush'ı bu boyutta parçalar (UX P1 I1). */
export const FORM_RESPONSE_BATCH_MAX = 500;

export const FormResponseBatchUpsertSchema = z.object({
  responses: z.array(FormResponseUpsertItemSchema).min(1).max(FORM_RESPONSE_BATCH_MAX),
});

/**
 * UX P1 I1 — rota katmanı zarfı: parti şekli doğrulanır, öğeler AYRI AYRI
 * (`FormResponseUpsertItemSchema`) doğrulanır ki tek bozuk öğe (ör. çok uzun not)
 * bütün partiyi 422'ye düşürmesin.
 */
export const FormResponseBatchEnvelopeSchema = z.object({
  responses: z.array(z.unknown()).min(1).max(FORM_RESPONSE_BATCH_MAX),
});
export type FormResponseBatchUpsert = z.infer<typeof FormResponseBatchUpsertSchema>;

// Response — success / stale_write / validation_failed / server_error
export const FormResponseBatchUpsertResultSchema = z.object({
  success: z.boolean(),
  savedAt: z.string().datetime().optional(),
  // UX P1 K4 — kısmi kabul: VALIDATION_FAILED'da geçerli öğeler yine yazılır; yazılan
  // soru kodları burada döner (yoksa hiçbir şey yazılmadı demektir).
  savedCodes: z.array(z.string()).optional(),
  // UX P1 I2 — aynı kullanıcının daha yeni damgalı yazımı zaten indiği için ATLANAN öğeler;
  // sunucudaki güncel değerle döner (istemci yerel kopyasını bununla eşitler).
  superseded: z
    .array(
      z.object({
        questionCode: z.string(),
        value: z.unknown().nullable(),
        consultantNote: z.string().nullable(),
        updatedAt: z.string().datetime(),
        // R1 — saklı istemci damgası: "Benimkini uygula" yeniden gönderimi bunun ÜSTÜNDE
        // damgalanır, yoksa (saati geride cihaz) yine atlanırdı.
        clientUpdatedAt: z.string().datetime().nullable(),
      }),
    )
    .optional(),
  // Failure: neden ve hangi item'lar
  errorCode: z.enum(["STALE_WRITE", "VALIDATION_FAILED", "SERVER_ERROR"]).optional(),
  details: z
    .array(
      z.object({
        questionCode: z.string(),
        // M15 DR6 — composite parça hatası: FE mesajı ilgili parçanın altına yazar
        partKey: z.string().optional(),
        message: z.string(),
        serverUpdatedAt: z.string().datetime().optional(),
      }),
    )
    .optional(),
});
export type FormResponseBatchUpsertResult = z.infer<typeof FormResponseBatchUpsertResultSchema>;

// GET /projects/:id/responses response
export const FormResponsesListResponseSchema = z.object({
  responses: z.array(FormResponseSchema),
});
export type FormResponsesListResponse = z.infer<typeof FormResponsesListResponseSchema>;

// Snapshot endpoint response shape (GET /projects/:id/snapshot)
// M2-S3 ledger devri — ProjectDetail + FormFill initial load için.
export const SectionSnapshotSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  order: z.number(),
  sectionPrompt: z.string().default(""),
});

export const QuestionSnapshotSchema = z.object({
  id: z.string().uuid(),
  sectionId: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  helperText: z.string().nullable(),
  // Tek kaynak: QuestionType. options bilerek z.unknown() — donmuş snapshot'ları
  // Zod'dan geçirmek geriye dönük risk; composite erişimi readCompositeParts'tan (D8).
  type: QuestionType,
  options: z.unknown(),
  required: z.boolean(),
  enabled: z.boolean(),
  // Opt-out (default false). Eski snapshot'larda bu alan yok (priorYearHint vardı) —
  // default sayesinde eski projelerde öneri her soruda görünür (istenen davranış).
  priorYearHintDisabled: z.boolean().default(false),
  allowEvidence: z.boolean(),
  allowNote: z.boolean(),
  aiContext: z.string().nullable().default(null),
  order: z.number(),
});

export const ProjectSnapshotResponseSchema = z.object({
  project: z.object({
    id: z.string().uuid(),
    frameworkName: z.string(),
    reportingYear: z.number(),
    // Raporlama dönemi (K7 geriye uyum): eski snapshot'larda alan yok — default "annual"
    // sayesinde eski projeler yıllık davranır (geriye dönük değişmez).
    periodType: PeriodType.default("annual"),
    // Aşağıdaki alanlar SADECE internal aktöre döner (davranış değişmedi).
    // Client aktöre (Task 5, eng-review D15 dar DTO) bu alanlar hiç gönderilmez —
    // Zod'da optional tutuluyor ki tek şema hem internal hem client yanıtını
    // type-check edebilsin (ayrı discriminated union'a gerek yok, tek tüketici
    // route zaten hangi actor'e ne döndüğünü biliyor).
    clientName: z.string().optional(),
    frameworkVersionId: z.string().uuid().optional(),
    versionNumber: z.string().optional(),
    assignedToName: z.string().optional(),
    status: z.enum(["draft", "in_progress", "completed"]).optional(),
    createdAt: z.string().datetime().optional(),
    // Client aktöre kendi participant durumu (D15 "kendi participant durumu").
    participantStatus: z.enum(PARTICIPANT_STATUSES).optional(),
  }),
  snapshot: z.object({
    systemPrompt: z.string(),
    frameworkPrompt: z.string(),
    sections: z.array(SectionSnapshotSchema),
    questions: z.array(QuestionSnapshotSchema),
  }),
});
export type ProjectSnapshotResponse = z.infer<typeof ProjectSnapshotResponseSchema>;
export type SectionSnapshot = z.infer<typeof SectionSnapshotSchema>;
export type QuestionSnapshot = z.infer<typeof QuestionSnapshotSchema>;

// Attachment schemas — plan §4.4.1 + §11.2 + §11.5 E7 (M4-S3).
export const AttachmentPresignRequestSchema = z.object({
  questionCode: z.string().min(1),
  fileName: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(128),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024),
});
export type AttachmentPresignRequest = z.infer<typeof AttachmentPresignRequestSchema>;

export const AttachmentPresignResponseSchema = z.object({
  attachmentId: z.string().uuid(),
  uploadUrl: z.string().url(),
  storagePath: z.string(),
  expiresIn: z.number(),
  scanStatus: z.literal("pending"),
});
export type AttachmentPresignResponse = z.infer<typeof AttachmentPresignResponseSchema>;

export const AttachmentInfoSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  // TODO-EVIDENCE-05: `error` = tarama tamamlanamadı (cron yeniden dener). Tek kaynak kpi.ts.
  scanStatus: z.enum(EVIDENCE_SCAN_STATUSES),
  uploadedAt: z.string().datetime(),
  // TODO-EVIDENCE-01: form ekleri evidence havuzunda — retention nesneyi sildiyse dolu
  // (kayıt + bağ kalır, indirilemez). Eski istemciler alanı görmezden gelir.
  purgedAt: z.string().nullable().optional(),
  // Backend proxy download URL (/api/v1/files/<storagePath>?filename=...).
  // Frontend bu URL'i <a href> ile direkt kullanır — Content-Disposition: attachment
  // header'ıyla browser otomatik download tetikler.
  downloadUrl: z.string(),
});
export type AttachmentInfo = z.infer<typeof AttachmentInfoSchema>;

export const AttachmentListResponseSchema = z.object({
  attachments: z.array(AttachmentInfoSchema),
});
export type AttachmentListResponse = z.infer<typeof AttachmentListResponseSchema>;

// M4-S4 — Missing fields (§4.4.2). M15: composite'te parça düzeyi raporlama (U4).
export const MissingFieldSchema = z.object({
  sectionId: z.string().uuid(),
  sectionCode: z.string(),
  sectionLabel: z.string(),
  questionCode: z.string(),
  questionLabel: z.string(),
  partKey: z.string().optional(),
  partLabel: z.string().optional(),
});
export type MissingField = z.infer<typeof MissingFieldSchema>;

export const MissingFieldsResponseSchema = z.object({
  missing: z.array(MissingFieldSchema),
});
export type MissingFieldsResponse = z.infer<typeof MissingFieldsResponseSchema>;

// M4-S4 — Prior year hints (§6.4). M15 §7.1: questionType ile FE tip uyum kapısı —
// soru tipi yıllar arası değiştiyse kutu hiç görünmez (yanlış şekil kopyalanamaz).
export const PriorYearEntrySchema = z.object({
  questionCode: z.string(),
  value: z.unknown(),
  reportingYear: z.number(),
  projectId: z.string().uuid(),
  questionType: QuestionType,
  // Raporlama dönemi (K7 geriye uyum): eski payload'larda alan yok — default "annual".
  periodType: PeriodType.default("annual"),
});
export type PriorYearEntry = z.infer<typeof PriorYearEntrySchema>;

export const PriorYearHintsResponseSchema = z.object({
  hints: z.array(PriorYearEntrySchema),
});
export type PriorYearHintsResponse = z.infer<typeof PriorYearHintsResponseSchema>;

// M4-S4 — SSE presence
export const PresenceUserSchema = z.object({
  userId: z.string().uuid(),
  fullName: z.string(),
});
export type PresenceUser = z.infer<typeof PresenceUserSchema>;

export const PresenceEventSchema = z.object({
  others: z.array(PresenceUserSchema),
});
export type PresenceEvent = z.infer<typeof PresenceEventSchema>;
